/**
 * THE SPOTLIGHT WALKTHROUGHS — one per screen, and that is the requirement.
 *
 * WHY EVERY SCREEN, EVEN THE DULL ONES
 * ------------------------------------
 * "Show me" is the question this feature answers, and a user cannot know in advance which
 * screens it has an answer for. A registry covering the nine interesting screens produces a
 * feature that works until the first time somebody needs it, which is worse than one that
 * does not exist — so `walkthroughs.test.ts` asserts a walkthrough for EVERY key in
 * `SCREEN_KEYS` and fails the build when a screen is added without one.
 *
 * WHAT A STEP IS ALLOWED TO SAY
 * -----------------------------
 * Every control named below was read out of the component that renders it, with the label
 * copied exactly as the JSX spells it. **Nothing here is inferred from what a screen "probably"
 * does.** That rule is not stylistic: the printed guides shipped instructions for
 * `components/Header/Header.tsx`, a seven-group manager menu that is imported by nothing and
 * describes a UI that has never existed (see AGENTS.md, "one live component, two dead ones").
 * A walkthrough is worse than a wrong sentence in a guide, because it points at the screen
 * while it says it — so a step that names a control the user cannot see reads as the app
 * being broken.
 *
 * Two consequences visible in the data below:
 *
 *  - There are no tours for the discontinued screens (barcode, the inspection reports, and the
 *    manager planning/quality prototypes — removed 2026-09-28) and none for `/choose-organization`,
 *    which CoSMo must never describe. Neither is in `SCREEN_KEYS`, so neither needs one.
 *  - Steps are plain text. No markdown, no HTML: `AnswerText`'s grammar belongs to the chat
 *    panel, and the overlay renders these into a small box as text nodes. A `**bold**` here
 *    would reach the user as four literal asterisks, exactly as it did in the panel before
 *    `AnswerText` existed.
 *
 * ANCHORS
 * -------
 * `anchor` is the value of a `data-csm-step` ATTRIBUTE on the real control. An attribute, not
 * an id and not a class:
 *
 *  - **Not an id.** Several of these controls are rendered many times at once — the card
 *    hamburger on every card, the write-back action buttons in every lane that offers them,
 *    the resource picker twice on User Settings. `CardActionMenu` carries the scar: per-card
 *    ids collided because one job legitimately appears in more than one column. Duplicates are
 *    fine here and are the intended reading — the overlay spotlights the first match, which is
 *    the first card / leftmost lane, and that is the one a walkthrough means.
 *  - **Not a class.** A class is indistinguishable from styling and gets tidied away by the
 *    next person to touch the stylesheet, silently. This registry would still be green and the
 *    walkthrough would simply stop finding anything.
 *
 * A missing anchor is NOT an error. The overlay skips the step, warns with the key, and
 * carries on — so a screen that is mid-refactor degrades to a shorter walkthrough instead of a
 * broken one. Several anchors below are deliberately on conditional controls (the
 * relationship map's tree needs a search) and are expected to
 * be skipped on a screen the user has just arrived at.
 *
 * ⚠️ THE CAP WAS FIVE STEPS, THEN TWELVE, AND IS NOW FIFTEEN — BECAUSE FIVE WAS THE WRONG
 * ANSWER TO THE RIGHT QUESTION (changed 2026-09-15, reported as "the spotlight tours are all
 * missing a lot of buttons"). The cap exists so a tour cannot grow into a manual nobody reads.
 * What five produced instead was a tour that named a third of each screen: `/home` in Shop
 * Floor view carries a search box, a show/hide toggle, four filter pickers, the chips, Clear
 * filters, the column headings, the board, the card menu and the two view tabs, and the tour
 * named five of them — while the filter bar, the capacity toolbar, the write-back lane actions
 * and several tab strips had carried anchors nothing pointed at since the day they were added.
 *
 * A tour that skips most of the screen is not a shorter tour, it is a wrong one: the user
 * cannot tell "we chose not to mention this" from "this is not part of the app", and the
 * question they pressed "?" to ask is usually about one of the controls that was left out.
 * Length is also the cheapest thing here to get out of: the overlay shows "Step 3 of 9", Next
 * is the only thing that advances it, and Escape or the close button ends it at any point — so
 * an over-long tour costs the reader one step, not the screen.
 *
 * ⚠️ TWELVE WAS RAISED TO FIFTEEN THE SAME DAY, AND THE REASON IS THE ARGUMENT ABOVE RATHER
 * THAN A NEW ONE. `/home` in Shop Floor view had reached exactly twelve, so the "describe what
 * to show" box added to the filter bar could not be named at all — and a cap that is reached by
 * the screen with the most controls on it stops being "don't write a manual" and starts being
 * "this screen may not be covered", which is the precise failure five was raised for. The
 * headroom is deliberately small: three steps is one more control on each of the two or three
 * screens that are genuinely dense, not licence for every tour to grow.
 *
 * Fifteen is still a cap and `walkthroughs.test.ts` still enforces it, along with a floor of
 * two. There are still a few more `data-csm-step` attributes in the app than there are steps
 * here, and that is still fine — an attribute that changes no behaviour is the cheapest thing
 * in this feature to have and the most tedious to go back for. The naming is `area.control`
 * throughout, so what exists is greppable: `git grep data-csm-step`.
 *
 * SCREEN KEYS, AND WHY EVERY WALKTHROUGH STARTS WITH ONE
 * ------------------------------------------------------
 * `screenKey` names the screen a step's control is on, and the overlay moves there before
 * running the step — turning Manager view on first, and saying so, when the destination needs
 * it. **The first step of every walkthrough declares its own screen**, and that is not
 * decoration. The chip that starts a walkthrough is offered in the chat panel from wherever the
 * user happens to be standing, so while no step declared one, "show me User Settings" asked
 * from the home board hunted for the User Settings controls ON the home board, found none of
 * them, and ended with the overlay's "I couldn't find those controls on this screen" line. The
 * feature worked only for someone already standing where they had just asked to be taken.
 *
 * Later steps omit it, because the first one has already arrived. The app chrome (header,
 * manager ribbon, filter bar) is rendered by the layout rather than by the page, so a step
 * about the Analytics tab or the Settings ribbon finds its target without moving again.
 *
 * `/home` IS THREE SCREENS, AND ONE STEP LIST CANNOT DESCRIBE ALL THREE
 * --------------------------------------------------------------------
 * `Home.tsx` renders the shop-floor Kanban, the Capacity Manager or the write-back approval
 * board depending on `isManagerMode` and `isActiveJobMode`. The screen table has ONE row for it
 * because the ROUTE is one route, and `screenForPath` could not tell two rows with the same
 * path apart — so the mode has to be read from the store, exactly as `screenHelp.ts` already
 * reads it to choose which question "Ask about this screen" sends.
 *
 * This file did not read it, and the cost was the whole feature on the app's busiest screen.
 * One five-step list named `board.kanban` and `board.card-menu` (shop floor only) alongside
 * `home.active-jobs-tab` (manager only), so WHICHEVER view the user was in, two or three of
 * the five steps pointed at controls that were not on their screen — four seconds of "Looking
 * for that part of the screen…" each — and the steps that did land narrated the other view.
 * Pressing "?" on the Active jobs screen gave a tour of the shop-floor board.
 *
 * So `/home` has THREE step lists and `walkthroughFor` picks between them from the two flags.
 * They still share the key `home` — the key is the screen and there is still exactly one
 * screen — which is what keeps `WALKTHROUGH_KEYS` equal to `SCREEN_KEYS` and keeps the Function
 * App's half of the contract untouched. Every step in each list names a control that is on
 * screen in THAT view, so none of the three stalls.
 *
 * Each list ends on the tab that leads to one of the others, and does NOT press it: the user
 * presses the real button (the overlay's rule 1), the tour ends there, and the "?" on the
 * screen they land on walks them through that one. Flipping the view out from under someone who
 * asked to be shown where they already are would be the overlay's rule 3 in reverse.
 *
 * WHEN A STEP'S CONTROL DEPENDS ON THE USER RATHER THAN THE SCREEN
 * ---------------------------------------------------------------
 * `requires` names the permission bit a step's control is rendered behind. The Manager and Shop
 * Floor tabs are the only such controls today (`CSMShopfloorHeader` renders each behind its own
 * bit), and a walkthrough that points at the other view's tab is exactly the step an operator
 * with only the shop-floor bit will never find. Declaring it lets the overlay skip that step
 * AT ONCE instead of waiting the full anchor timeout for an element that cannot arrive.
 *
 * It is NOT an access check and must never be used as one — `canReachScreen` is the gate, and
 * this only ever makes a tour shorter and quicker.
 *
 * The five parameterised screens (`job`, `job-routing`, `job-bom`, `job-images`,
 * `job-documents`) declare theirs too, and the overlay
 * still refuses to navigate to them: their paths need a production order
 * this registry has no way to know, and guessing one would be a new way to show somebody data
 * they did not ask for. Declaring it there is what lets the overlay tell "you are on that order
 * already, run normally" apart from "you are not, so say which door to open first" — without
 * it, the second case spotlighted nothing, a step at a time, and then apologised.
 */

import { SCREEN_KEYS } from "./routes";

export interface WalkthroughStep {
  /** The `data-csm-step` value of the control this step points at. */
  readonly anchor: string;

  /**
   * Where this step's control lives. Omitted means "wherever the user already is", which is
   * what every step after the first wants — the first one has already taken them there.
   *
   * A key here must be a real screen key, and the overlay re-checks `canReachScreen` for it
   * before navigating: a walkthrough must never be the thing that carries someone onto a
   * screen their permissions do not allow.
   */
  readonly screenKey?: string;

  /**
   * The permission bit this step's CONTROL is rendered behind, when it has one.
   *
   * Omitted means "everyone on this screen sees it", which is almost every step. See the header
   * comment: this exists so a step pointing at the Manager or Shop Floor tab is skipped
   * immediately for a user who does not have that tab, rather than spending the anchor timeout
   * waiting for an element the header will never render.
   */
  readonly requires?: "manager" | "shopfloor";

  /** One short line, shown as the step's heading. */
  readonly title: string;

  /** PLAIN TEXT. No markdown, no HTML — see the header comment. */
  readonly body: string;
}

export interface Walkthrough {
  /** Always a screen key from `routes.ts`. There is no second namespace. */
  readonly key: string;

  /** How the walkthrough offers itself, e.g. in a chip or a menu entry. */
  readonly label: string;

  readonly steps: readonly WalkthroughStep[];
}

const WALKTHROUGHS: readonly Walkthrough[] = Object.freeze([
  // =====================================================================================
  // Open to anyone signed in
  // =====================================================================================
  // `/home` in SHOP FLOOR view. The other two views are `HOME_MANAGER_ACTIVE` and
  // `HOME_MANAGER_COMPLETED`, below the array — `walkthroughFor` picks between the three from
  // the mode flags. This one is in the array because it is the answer with no context supplied,
  // which is also what the registry's own tests read.
  {
    key: "home",
    label: "Show me around the Shop Floor board",
    steps: [
      {
        anchor: "header.shopfloor-tab",
        screenKey: "home",
        title: "Shop Floor view",
        body: "Shop Floor is the technician's view. It shows the board of released production orders, with a column for every resource work passes through and a card sitting under the resource it is waiting at.",
      },
      {
        anchor: "search.toggle",
        title: "Search and filters",
        body: "The plus at the right-hand end of this row opens and closes the search box and the filter bar underneath it. Closing it also clears every filter you have set, so the board goes back to showing everything.",
      },
      {
        anchor: "search.input",
        title: "Search all production orders",
        body: "Type here and press Enter, or press the magnifier beside the box, to cut the board down to the orders that match. Emptying the box puts the whole board back.",
      },
      {
        // Placed between the search box and the pickers because that is what it sits between,
        // and because the two questions either side of it are the ones people confuse: the box
        // above finds an order you can name, this one narrows the board when you cannot. It is
        // also the one control in this tour that is not always rendered — where the assistant
        // feature is off, or this region's Function App has no model deployment, the box is not
        // drawn and the overlay steps over this in a quarter of a second (SKIP_AHEAD_MS).
        anchor: "filter.assist",
        title: "Describe what to show",
        body: "Say what you want to see in plain words — late orders for Acme in warehouse 01 — and the filters below are set for you. Every one of them becomes a chip you can read and remove, and anything it could not match is named underneath, so nothing is hidden from you without being said.",
      },
      {
        anchor: "filter.resource",
        title: "Select resource filter",
        body: "This cuts the board down to one area's resources, using the filters a manager set up on Resource Settings. It is the quickest way to get from the whole site to your own bench.",
      },
      {
        anchor: "filter.warehouse",
        title: "Warehouse and BP name",
        body: "Warehouse narrows the board to one site's orders and Select BP name narrows it to one customer. Both are lists you pick from, and a value you have already picked drops out of the list so it cannot be added twice.",
      },
      {
        anchor: "filter.due",
        title: "Due date",
        body: "Select due date from/to has two shortcuts, Today and This Week, and the arrow beside them opens From and To boxes for any other range with Cancel and OK underneath. This Week is applied for you when the board opens.",
      },
      {
        anchor: "filter.chips",
        title: "What you have filtered by",
        body: "Every pick becomes a chip along here with its own cross, so one can be dropped without losing the rest. Clear filters, at the right-hand end of the row, removes all of them at once and only appears while something is set.",
      },
      {
        anchor: "board.column-header",
        title: "The column headings",
        body: "A heading carries the resource's name, Items — how many orders are waiting at it — and the totals of Time remaining and Time overrun across those orders. The two completion columns at the far end show the count on its own.",
      },
      {
        anchor: "board.kanban",
        title: "The board",
        body: "Each card moves by itself to the next resource as each one is marked complete. A column with nothing in it is hidden, and one order can have a card in more than one column if it is open at more than one resource. At the far end are two columns that are not resources: All operations complete, and Production order complete.",
      },
      {
        anchor: "board.card-menu",
        title: "The card menu",
        body: "This button opens the actions for that order without leaving the board: View job details, Start operation, Stop operation, Complete resource, Add documents, Add notes and Add images. Which entries appear depends on the state the card is in.",
      },
      {
        anchor: "header.account",
        title: "Your account",
        body: "Your name at the right-hand end of the top bar opens a small menu with Log out in it. Images and Documents beside it open every picture and file attached across all production orders, not just the one you are on.",
      },
      {
        // Only rendered for a user who holds the manager bit, hence `requires` — an operator
        // skips straight past it rather than waiting out the anchor timeout on the last step.
        anchor: "header.manager-tab",
        requires: "manager",
        title: "Manager view",
        body: "Manager view is the planning side, and it is a different board: Active jobs for priorities, due dates and capacity, Completed jobs for approving what goes back to SAP B1. Press Manager to switch to it, then press ? there and I'll walk you through that screen.",
      },
    ],
  },

  {
    key: "job",
    label: "Show me the production order details screen",
    // The `job.*` anchors below are declared inline in `pages/JobPage/JobPage.tsx` — the ribbon
    // bar, every one of its seven items, the header details block and the two grids. The ribbon
    // items other than Start and Complete resource were anchored in the same pass that lifted
    // the step cap: the tour used to name three of the seven and describe the rest in prose,
    // which is the shape of the "missing a lot of buttons" report.
    //
    // ⚠️ They are the reason a walkthrough must never assume its anchors exist. A missing one
    // ends the tour with "I couldn't find those controls", which is the designed behaviour for
    // an anchor that has rotted against a restyled screen — not a crash, and not a lie.
    steps: [
      {
        anchor: "job.ribbon",
        screenKey: "job",
        title: "The ribbon",
        body: "Everything you can do to this order is along the top: Back, Start, Stop, Complete resource, Rework, Notes and Production order complete. A greyed-out item is one the order's current state does not allow.",
      },
      {
        anchor: "job.back",
        title: "Back",
        body: "Back returns you to wherever you opened this order from, which is usually the board. Nothing on this screen is lost by pressing it — every action here saves as you go.",
      },
      {
        anchor: "job.start",
        title: "Start",
        body: "Start asks which resource you are working on and begins logging time against it. The popup also offers Log past job for work you have already done, with From and To boxes that stay greyed out until you tick it.",
      },
      {
        anchor: "job.stop",
        title: "Stop",
        body: "Stop ends the time you are logging. It is only available while something is running, so on an order nobody has started it is greyed out.",
      },
      {
        anchor: "job.complete-resource",
        title: "Complete resource",
        body: "Complete resource finishes the resource you are on and moves the order to the next one. It asks you to confirm which resource first, so nothing is completed by a single press.",
      },
      {
        anchor: "job.rework",
        title: "Rework",
        body: "Rework sends a completed resource back, and asks for a reason from the list a manager set up on Resource Settings. Ticking a resource also reopens every resource after it, and a note and a quality email go out with it.",
      },
      {
        anchor: "job.notes",
        title: "Notes",
        body: "Notes files a comment against this order with your name and the time on it. It is where a corrected time, a hold-up or anything the next person needs to know belongs.",
      },
      {
        anchor: "job.complete-order",
        title: "Production order complete",
        body: "This is the last step: it reports the finished quantity for the whole order rather than for one resource, and it is what sends the order on to a manager for approval. It stays greyed out until the order's state allows it.",
      },
      {
        anchor: "job.header-details",
        title: "What this order is",
        body: "The block under the ribbon is read-only: the product and sales order numbers, the description, the planned quantity, the quantity completed by operators and the quantity reported complete. The coloured dot is the order's status, and when the order is linked to others This PW has linked PW opens the relationship map.",
      },
      {
        anchor: "job.resources-grid",
        title: "Resources",
        body: "Resources lists every resource on the order in routing order, with who completed it, the planned and logged time, and the difference. Over/under is red when more time was taken than planned and green when less.",
      },
      {
        anchor: "job.components-grid",
        title: "Components",
        body: "Components is the parts list, and it is where you find out whether the job can be built: required quantity, issued and allocated quantities, ATP, the date the material is expected, and any incoming purchase orders.",
      },
    ],
  },

  {
    key: "job-routing",
    label: "Show me the resources list",
    steps: [
      {
        anchor: "routing.grid",
        screenKey: "job-routing",
        title: "The resources table",
        body: "One row per resource on this order, in routing order. It shows who completed each one, the planned time, the time logged against it, the over or under difference, and the date it was completed. The logged time can be corrected here.",
      },
      {
        anchor: "routing.note",
        title: "Add a note",
        body: "Anything typed here is filed against the order with your name and the time, so a corrected time has a reason attached to it.",
      },
      {
        anchor: "routing.submit",
        title: "Update and submit",
        body: "This saves the times and the note together. Nothing is sent until you press it.",
      },
    ],
  },

  {
    key: "job-bom",
    label: "Show me the parts list",
    steps: [
      {
        anchor: "bom.header-fields",
        screenKey: "job-bom",
        title: "What this order is",
        body: "The rows along the top are read-only: the stock code and description being built, the quantity ordered and built, and the customer and order numbers it came from.",
      },
      {
        anchor: "bom.grid",
        title: "Picking the components",
        body: "Each row is a component, with the quantity ordered, the quantity already picked and what is left. Tick the rows you have picked using the checkbox column on the left.",
      },
      {
        anchor: "bom.update",
        title: "Update",
        body: "Update saves what you have ticked. Until you press it, nothing has been recorded.",
      },
    ],
  },

  {
    key: "job-images",
    label: "Show me the images for this order",
    steps: [
      {
        anchor: "job-images.orders",
        screenKey: "job-images",
        title: "Pick the order",
        body: "The list on the left is this order and its parts, with the number of images held against each. Pick one and its pictures appear on the right.",
      },
      {
        anchor: "job-images.add",
        title: "Add",
        body: "Add uploads a picture from this device against the order selected on the left. Do not upload sensitive or confidential images.",
      },
      {
        anchor: "job-images.capture",
        title: "Capture",
        body: "Capture takes the photograph here using the device's camera, which is the quicker option on a shop-floor tablet.",
      },
      {
        anchor: "job-images.search",
        title: "Search all images",
        body: "This narrows the tiles on the right to pictures whose file name matches what you type. It searches the order you have picked, not every order.",
      },
      {
        anchor: "job-images.grid",
        title: "The pictures",
        body: "Click a picture to open it full size. Each tile also carries its own delete button.",
      },
    ],
  },

  {
    key: "job-documents",
    label: "Show me the documents for this order",
    steps: [
      {
        anchor: "job-documents.orders",
        screenKey: "job-documents",
        title: "Pick the order",
        body: "The list on the left is this order and its parts, with the number of documents held against each. Add stays greyed out until you pick one.",
      },
      {
        anchor: "job-documents.add",
        title: "Add",
        body: "Add attaches a file to the order selected on the left. Do not upload sensitive or confidential files.",
      },
      {
        anchor: "job-documents.search",
        title: "Search",
        body: "This narrows the tiles on the right to files whose name matches what you type.",
      },
      {
        anchor: "job-documents.grid",
        title: "The documents",
        body: "Click a tile to open the file. PDFs, images and plain text preview in the app; anything else downloads. Each tile has its own delete button.",
      },
    ],
  },

  {
    key: "similar-jobs-images",
    label: "Show me images from similar jobs",
    steps: [
      {
        anchor: "similar-jobs.orders",
        screenKey: "similar-jobs-images",
        title: "The part production orders",
        body: "Each row is another production order for the same part, with its stock code and how many images it has. Click one to load its pictures.",
      },
      {
        anchor: "similar-jobs.images",
        title: "The images",
        body: "The list on the right shows the pictures for the order you picked, each with its file name. Click one to open it.",
      },
      {
        anchor: "similar-jobs.download",
        title: "Download",
        body: "Download saves the selected picture. It stays greyed out until exactly one image is selected, because it saves one at a time.",
      },
    ],
  },

  {
    key: "images",
    label: "Show me the Images screen",
    steps: [
      {
        anchor: "header.images",
        screenKey: "images",
        title: "Images",
        body: "This screen shows every picture attached across all production orders, rather than just the one you are on. The button is in the top bar in either mode.",
      },
      {
        anchor: "images.orders",
        title: "The orders",
        body: "One row per production order, with its item number and how many images it holds. Pick one to see its pictures on the right.",
      },
      {
        anchor: "images.add",
        title: "Add",
        body: "Add uploads a picture from this device against the order selected on the left. Do not upload sensitive or confidential images.",
      },
      {
        anchor: "images.capture",
        title: "Capture",
        body: "Capture takes the photograph here using the device's camera.",
      },
      {
        anchor: "images.search",
        title: "Search all images",
        body: "This searches across every picture on the screen by file name, which is the quickest way to find one when you do not know which order it was filed under.",
      },
      {
        anchor: "images.grid",
        title: "The pictures",
        body: "Click a picture to open it full size. Each tile carries its own delete button, and the tiles change as you pick a different order on the left.",
      },
    ],
  },

  {
    key: "documents",
    label: "Show me the Documents screen",
    steps: [
      {
        anchor: "header.documents",
        screenKey: "documents",
        title: "Documents",
        body: "This screen shows every file attached across all production orders. The button is in the top bar in either mode.",
      },
      {
        anchor: "documents.orders",
        title: "The orders",
        body: "One row per production order, with its item number and how many documents it holds. Add stays greyed out until you pick one.",
      },
      {
        anchor: "documents.add",
        title: "Add",
        body: "Add attaches a file to the order selected on the left. Do not upload sensitive or confidential files.",
      },
      {
        anchor: "documents.search",
        title: "Search all documents",
        body: "This searches every file on the screen by name, which is the quickest way to find one when you do not know which order it was filed under.",
      },
      {
        anchor: "documents.grid",
        title: "The files",
        body: "Click a tile to open it. PDFs, images and plain text preview in the app; anything else downloads. Each tile has its own delete button.",
      },
    ],
  },

  // =====================================================================================
  // Manager settings
  // =====================================================================================
  {
    key: "settings-system",
    label: "Show me System Settings",
    steps: [
      {
        anchor: "ribbon.system",
        screenKey: "settings-system",
        title: "The Settings ribbon",
        body: "In Manager view the ribbon above the page moves between the three settings screens: System, Resources and User.",
      },
      {
        anchor: "header.system-gear",
        title: "Getting here",
        body: "The gear at the right-hand end of the top bar comes straight to this screen. It is only in the bar while you are in Manager view.",
      },
      {
        anchor: "system.tab-integration",
        title: "Integration",
        body: "Two sub-tabs. General holds the addresses that completion and quality emails go to; SAP B1 holds the due date warning and automatic writeback.",
      },
      {
        anchor: "system.subtab-sapb1",
        title: "SAP B1",
        body: "Automatic writeback decides whether CSM issues resources, reports the completion and closes the order for you. It only does so when the reported quantity matches the planned quantity and the logged time is inside the tolerance in the box underneath. Changing it affects the whole site.",
      },
      {
        anchor: "system.tab-localization",
        title: "Localization",
        body: "The date and number formats the whole site sees. Changing them here changes how every date and quantity is written everywhere in CSM.",
      },
      {
        anchor: "system.tab-planning",
        title: "Planning",
        body: "Planning holds the warehouses, which are what the Warehouse filter on the board and the warehouse picker in Manager view offer.",
      },
      {
        anchor: "system.new-warehouse",
        title: "Adding a warehouse",
        body: "Type into Name of new warehouse and press Add, or press Enter. The list underneath is what you have already, and the bin beside a row removes it.",
      },
      {
        anchor: "system.save",
        title: "Each tab saves on its own",
        body: "This button saves the tab you are on and nothing else, so changes made on another tab need saving there too. It reads Update while there is something unsaved, and Cancel beside it puts the tab back as it was.",
      },
    ],
  },

  {
    key: "settings-users",
    label: "Show me User Settings",
    steps: [
      {
        anchor: "ribbon.user",
        screenKey: "settings-users",
        title: "User Settings",
        body: "This is where the people who can use CSM are set up. You need Manager permission to be here at all.",
      },
      {
        anchor: "users.new-name",
        title: "Add a user",
        body: "Type the name and press Add. The new user appears in the list below, and their details are filled in on the right.",
      },
      {
        anchor: "users.list",
        title: "The users",
        body: "Click anyone to edit them. A user marked Inactive keeps their record and their history but can no longer sign in, which is what to use instead of deleting someone.",
      },
      {
        anchor: "users.details",
        title: "User details",
        body: "The name, the email address CSM writes to, and the Inactive tick. A user marked Inactive shows an Inactive badge in the list and can no longer sign in.",
      },
      {
        anchor: "users.permissions",
        title: "User permissions",
        body: "Manager and Shop Floor are the two permissions, and a user can hold either or both. Manager is what opens these settings screens and Manager view; Shop Floor is what opens the board.",
      },
      {
        anchor: "users.tabs",
        title: "Resources and Availability",
        body: "Two tabs under the permissions. Resources is which resources this person may work on, and Availability is their working hours, which is what the Capacity Manager plans against.",
      },
      {
        anchor: "users.resources",
        title: "Log time and Complete job",
        body: "The Resources tab holds two of these pickers side by side, Log time and Complete job, so someone can be allowed to work at a resource without being allowed to sign it off. Each has its own Search box and a Select all.",
      },
      {
        anchor: "users.save",
        title: "Save",
        body: "Nothing is saved until this is pressed. It reads Update while there is something unsaved, and Saving while it is in flight. Cancel beside it discards the edits.",
      },
    ],
  },

  {
    key: "settings-operations",
    label: "Show me Resource Settings",
    steps: [
      {
        anchor: "ribbon.resources",
        screenKey: "settings-operations",
        title: "Resource Settings",
        body: "Four tabs across the top, each with its own save button: General, Production, Resource filters and Rework.",
      },
      {
        anchor: "operations.tab-general",
        title: "General",
        body: "Two site-wide rules live here: Allow completing resource out of sequence, which lets an operator finish a resource that is not the earliest one still open, and Do not allow users to log past job, which removes the Log past job option from the Start popup.",
      },
      {
        anchor: "operations.tab-production",
        title: "Production",
        body: "The resources themselves, and the order they appear in as columns on the Shop Floor board.",
      },
      {
        anchor: "operations.new-resource",
        title: "Adding a resource",
        body: "Type a name into this box and press Add. The new resource appears in the list below, where its sort order is set on the right.",
      },
      {
        anchor: "operations.tab-filters",
        title: "Resource filters",
        body: "A filter is a named group of resources, and it is what an operator picks on the home screen to narrow the board to their own area.",
      },
      {
        anchor: "operations.new-filter",
        title: "Adding a filter",
        body: "Type a name into New resource filter and press Add. Then build it up on the right by moving resources between Unassigned and Assigned with the arrows.",
      },
      {
        anchor: "operations.tab-rework",
        title: "Rework",
        body: "The list of reasons offered when someone sends a resource back for rework. Until there is at least one here the rework popup has nothing to offer and says so.",
      },
      {
        anchor: "operations.new-rework-reason",
        title: "Adding a reason",
        body: "Type into New reason for rework and press Add, or press Enter. The box stops you at the longest reason that can be stored, and a reason already in the list is refused with a message. An added reason is not saved until the tab is.",
      },
      {
        anchor: "operations.save",
        title: "Each tab saves on its own",
        body: "This saves the tab you are on, and Cancel beside it puts that tab back as it was. Moving to another tab with something unsaved asks you first.",
      },
    ],
  },

  // =====================================================================================
  // Analytics and the organisation picker
  // =====================================================================================
  {
    key: "analytics",
    label: "Show me how to build a report",
    steps: [
      {
        anchor: "header.analytics-tab",
        screenKey: "analytics",
        title: "Analytics",
        body: "Reporting is switched on per site, so this tab is only in the top bar where it has been enabled. Building, saving and sharing reports needs Manager permission; without it you can still open and run a report shared with you.",
      },
      {
        anchor: "report.saved-list",
        title: "Saved reports",
        body: "Your own reports and any shared with you. Click one to open it. Before you have saved any, the list reads No saved reports yet.",
      },
      {
        anchor: "report.new",
        title: "New report",
        // The one step in the registry that asks the user to press the control rather than
        // Next, and it is not a flourish: everything after it lives inside the builder, which
        // does not exist until this is pressed (`ReportPage`: `!builderOpen ?`). Pressing it
        // advances the tour by itself — the overlay watches the real control — and pressing
        // Next instead skips ahead to the guide, which is the next thing that is on the screen.
        body: "The builder does not open until you press this. Build from existing beside it starts from a copy of a report you already have. Press New report now, and the rest of this tour is the builder it opens.",
      },
      {
        // Placed AFTER report.new rather than before it, although it sits lower in the
        // sidebar: this step is about a second way to reach the same builder, and it
        // only makes sense once the tour has said what the builder is. It is also the
        // one control here that is not always rendered — where the assistant feature is
        // off, or this region's Function App has no model deployment, the box is not
        // drawn at all and the overlay steps over this in a quarter of a second (see
        // SKIP_AHEAD_MS in WalkthroughOverlay).
        anchor: "report.assist",
        title: "CoSMo Builder",
        body: "Type what you want in plain words — completed jobs by resource last month, as a bar chart — and the builder is filled in for you: the table, the columns, the filters and the grouping. Nothing is run and nothing is saved, so check it over and press Execute yourself.",
      },
      {
        anchor: "report.table-picker",
        title: "Choose a table",
        body: "Everything else stays empty until a table is chosen. After that, Fields to display picks the columns, Joins brings in a second table, Filters narrows the rows and Group by turns a list into totals.",
      },
      {
        anchor: "report.execute",
        title: "Execute",
        body: "Execute runs the report and fills the Table and Chart tabs. Give it a name in place of Untitled report in the box beside it before saving, or it saves untitled.",
      },
      {
        anchor: "report.tabs",
        title: "Builder, Table and Chart",
        body: "These three tabs are the same report seen three ways: Builder is where it is defined, Table is the rows it returned, and Chart draws them. Builder is only there if you have Manager permission.",
      },
      {
        anchor: "report.save",
        title: "Save",
        body: "Save puts the report in the Saved reports list on the left so it can be run again. It saves the definition, not the rows, so a saved report is up to date every time it is opened.",
      },
      {
        anchor: "report.share",
        title: "Share",
        body: "Share gives named people access to this report. It shows the number of people it is shared with once there is one, and someone it is shared with can open and run it without being able to change it.",
      },
      {
        anchor: "report.export",
        title: "Export",
        body: "Export takes the results out of CSM as Excel, CSV or PDF. It acts on what the Table tab is showing, so it stays greyed out until the report has been run and you are on that tab.",
      },
      {
        anchor: "report.guide",
        title: "How to build a report",
        body: "This opens a written guide to the builder in place of the workspace. Your report is untouched underneath, and closing the guide puts you back exactly where you were.",
      },
    ],
  },

  // =====================================================================================
  // Manager-area screen. The relationship map is reached from the Manager ribbon and table.
  // =====================================================================================
  {
    key: "hierarchical",
    label: "Show me the relationship map",
    steps: [
      {
        anchor: "hierarchical.search",
        screenKey: "hierarchical",
        title: "Find an order",
        body: "Type a production order number and press Enter or the search button. Nothing is drawn until you do. The cross clears what you typed.",
      },
      {
        anchor: "hierarchical.tree",
        title: "What it draws",
        body: "The order you searched for, with everything linked to it underneath — useful when a sub-assembly is holding up a parent order. Each card shows the planned quantity, the time logged and the time remaining, and double-clicking one opens that order's details screen.",
      },
      {
        anchor: "hierarchical.view-parent",
        title: "View parent",
        body: "When the order you searched for is itself part of something larger, this appears and takes you up a level. If the number does not exist the screen says so rather than drawing an empty map.",
      },
    ],
  },

]);

/**
 * `/home` IN MANAGER VIEW, ACTIVE JOBS — the Capacity Manager (`Home.tsx`: Manager + Active).
 *
 * Not in the array above: `WALKTHROUGH_KEYS` is a list of SCREENS, and this is the same screen
 * seen in a different view. `walkthroughFor` returns it for `home` when both flags are on.
 *
 * Every anchor here is on `CSMManagerModeToggle`, `CSMTableControls` or `CapacityManagerPage`,
 * all three of which render only in this view — so nothing in this list can be hunted for on a
 * board that is not showing it.
 */
const HOME_MANAGER_ACTIVE: Walkthrough = Object.freeze({
  key: "home",
  label: "Show me around the Active jobs screen",
  steps: Object.freeze([
    {
      anchor: "home.active-jobs-tab",
      screenKey: "home",
      title: "Active jobs",
      body: "Manager view is the planning side, and Active jobs — the tab you are on — is its planning table: every production order due, with the priorities, due dates and releases you set on it.",
    },
    {
      anchor: "capacity.grid",
      title: "The table",
      body: "One row per production order, grouped by the week it is due, with the product, description and BP name alongside the planned quantity and the time scheduled, logged and remaining. Three cells are editable in place: PW priority, Due date, and Status, which goes from P to R to release a planned order and can only be changed while it still reads P. An edited cell is marked until it is saved, and double-clicking a row opens that order's details screen.",
    },
    {
      anchor: "capacity.show-planned",
      title: "What the table is showing",
      body: "Show planned production orders adds the orders that have not been released yet, which are hidden by default. Show one month only limits the table to the coming month.",
    },
    {
      anchor: "capacity.warehouse",
      title: "Select warehouse",
      body: "This narrows the table to one warehouse's orders. The count beside it, Active jobs, is how many rows the table is showing with everything above applied.",
    },
    {
      anchor: "capacity.last-sync",
      title: "Last sync",
      body: "When CSM last took data from SAP B1. Anything released or changed in SAP B1 since then is not in this table yet.",
    },
    {
      anchor: "capacity.relationship-map",
      title: "View relationship map",
      body: "This opens the linked-orders screen, which draws a production order with everything linked to it underneath — useful when a sub-assembly is holding up a parent order.",
    },
    {
      anchor: "capacity.import",
      title: "Import",
      body: "Import asks SAP B1 for a fresh pull rather than waiting for the scheduled one. Use it when an order you know exists is not in the table.",
    },
    {
      anchor: "capacity.reset",
      title: "Reset",
      body: "Reset undoes the edits you have not saved and puts the table back to what CSM holds. It does not undo anything already committed.",
    },
    {
      anchor: "capacity.commit",
      title: "Commit",
      body: "Commit saves every row you have edited, one order at a time; with nothing edited it tells you there is nothing to commit.",
    },
    {
      anchor: "home.completed-jobs-tab",
      title: "Completed jobs",
      body: "The other half of Manager view: the approval step before anything is written back to SAP B1, and the number beside it is how many orders are waiting for you. Press it to go there, then press ? and I'll walk you through that board.",
    },
  ]),
});

/**
 * `/home` IN MANAGER VIEW, COMPLETED JOBS — the write-back approval board (`CSMWriteBack`).
 *
 * The three lane action buttons live in the Kanban's COLUMN TEMPLATE, so each is rendered once
 * per lane that offers it and the overlay spotlights the leftmost — which is the one the step
 * means. See `CSMWriteBack.ActionButtonProps.anchorKey`.
 */
const HOME_MANAGER_COMPLETED: Walkthrough = Object.freeze({
  key: "home",
  label: "Show me around the Completed jobs board",
  steps: Object.freeze([
    {
      anchor: "home.completed-jobs-tab",
      screenKey: "home",
      title: "Completed jobs",
      body: "This is Production Order Completion Approval: the step between an order finishing on the shop floor and anything being written back to SAP B1. The number on the tab is how many orders are waiting here.",
    },
    {
      anchor: "writeback.kanban",
      title: "The four lanes",
      body: "An order moves left to right as you approve it: Operations complete is what the shop floor has finished, Partially issued is what has had some of its resources issued, Complete has been reported to SAP B1, and Closed is finished with.",
    },
    {
      anchor: "writeback.select-all",
      title: "Choosing what to send",
      body: "Every card has its own tick box, and this one in the lane heading ticks or clears the whole lane at once. Nothing is sent until you press one of the actions underneath.",
    },
    {
      anchor: "writeback.issue",
      title: "Issue resources only",
      body: "This issues the components for the cards ticked in its own lane and stops there — the order is not reported complete and not closed. The number beside the label is how many cards the lane holds.",
    },
    {
      anchor: "writeback.report",
      title: "Report completion",
      body: "This reports the finished quantity to SAP B1 for the ticked cards, which is what moves them into the Complete lane. It refuses with a message if nothing in that lane is ticked.",
    },
    {
      anchor: "writeback.close",
      title: "Close production orders",
      body: "The last step: it closes the ticked orders in SAP B1 and they leave the board. Closing is offered from every lane that can still be closed, so an order can be closed without being reported through CSM first.",
    },
    {
      anchor: "writeback.open-resources",
      title: "Checking a card first",
      body: "On a card that still has resources to issue, this opens the list of them so you can see what would be sent before you tick anything. It is not shown on cards that have nothing left to issue.",
    },
    {
      anchor: "writeback.card-menu",
      title: "The card menu",
      body: "The same actions the shop floor has, on the approval board: View job details, Add notes, Add images and Add documents. It is how you answer a question about an order without leaving the board.",
    },
    {
      anchor: "home.active-jobs-tab",
      title: "Back to Active jobs",
      body: "Active jobs is the other half of Manager view: the planning table where priorities, due dates and releases are set. Press it to go there, then press ? and I'll walk you through it.",
    },
  ]),
});

/** Every walkthrough key. Equal to `SCREEN_KEYS` as a set — pinned by the test. */
export const WALKTHROUGH_KEYS: readonly string[] = Object.freeze(
  WALKTHROUGHS.map((w) => w.key)
);

const BY_KEY: ReadonlyMap<string, Walkthrough> = new Map(
  WALKTHROUGHS.map((w) => [w.key, w])
);

/**
 * The two `appSettings` view flags, for the one screen whose walkthrough depends on them.
 *
 * Both are optional and both default the way the store does (`Home.tsx`: `isManagerMode ?? false`,
 * `isActiveJobMode ?? true`), so a caller that does not care — the registry's own tests, and any
 * future caller that only wants to know whether a tour exists — gets the shop-floor board and
 * never has to reach into the store to ask a question about a different screen.
 *
 * ⚠️ THESE ARE VIEW FLAGS, NOT PERMISSIONS. They can only ever change WHICH of the three
 * `/home` tours comes back. What a user is allowed to be shown is `canReachScreen`'s alone.
 */
export interface WalkthroughContext {
  readonly isManagerMode?: boolean;
  readonly isActiveJobMode?: boolean;
}

/**
 * Look a walkthrough up by screen key, and — for `/home` — by the view the user is in.
 *
 * Unknown keys return null rather than throwing or guessing — the caller may be relaying a
 * key the model emitted, and a marker naming a screen that has since been renamed must end in
 * nothing happening, not in an error on an operator's screen.
 *
 * This performs no access check. `canReachScreen` is the gate and it lives in the overlay,
 * where the user slice is readable; a second copy here would be a second answer to keep
 * correct. Getting a walkthrough back therefore does NOT mean the user may be shown it.
 */
export const walkthroughFor = (
  key: string | null | undefined,
  context?: WalkthroughContext
): Walkthrough | null => {
  const walkthrough = (key && BY_KEY.get(key)) || null;

  if (!walkthrough || walkthrough.key !== "home" || !context?.isManagerMode) {
    return walkthrough;
  }

  // Manager view. The second flag chooses between its two boards, and defaults to Active the
  // way the store does — `isActiveJobMode` is `?? true` everywhere it is read.
  return context.isActiveJobMode === false ? HOME_MANAGER_COMPLETED : HOME_MANAGER_ACTIVE;
};

/**
 * Re-exported so a caller that wants to offer "every walkthrough this user can reach" does
 * not have to import two modules to do it, and cannot accidentally iterate one list while
 * gating on the other.
 */
export { SCREEN_KEYS };
